import styled from 'styled-components';

import {getLocalizationService} from '~/shared/services/localisationService';
import ClickOutsideDiv from '~/shared/components/ClickOutsideDiv';
import {getBoldedAddressPartHtml} from '~/shared/utils/address';

import {
  DropdownContainer,
  Input,
  SuggestionItem,
  BoldResultContainer,
  PoweredByGoogleWrapper,
  PoweredByGoogleImg,
  SuggestionItemIcon,
  ScreenReaderNotificator,
  StyledSearchIconSvg,
} from './CustomAddressAutoComplete.styled';
import useAddressAutocomplete from './useAddressAutocomplete';
import {CURRENT_LOCATION_PLACE_ID} from './consts';

const Root = styled(ClickOutsideDiv)`
  width: 100%;
`;

const SuggestionResultWithBoldParts = ({matchedSubstrings, description}: {
  matchedSubstrings: google.maps.places.StringRange[];
  description: string;
}) => {
  if (!matchedSubstrings) {
    return <>{description}</>;
  }

  return (
    <BoldResultContainer
      dangerouslySetInnerHTML={{__html: getBoldedAddressPartHtml(matchedSubstrings, description)}}
    />
  );
};

type CustomAddressAutoCompleteProps = {
  inputRef: (domEl: HTMLInputElement) => void;
  placeholder?: string;
  resetAddress?: () => void;
  isMaxMobile?: boolean;
  isAddAddressMode?: boolean;
  onCurrentLocationClick?: ({setAddress}: {setAddress: (address: string) => void}) => Promise<void>;
  onPlaceSelected: (place: google.maps.places.PlaceResult) => void;
};

const CustomAddressAutoComplete = ({
  inputRef,
  placeholder,
  resetAddress,
  isMaxMobile,
  isAddAddressMode,
  onCurrentLocationClick,
  onPlaceSelected,
}: CustomAddressAutoCompleteProps) => {
  const {t} = getLocalizationService();
  const {
    address,
    isLoaded,
    isInputFocused,
    currentAddressKey,
    predictions,
    highlightedIndex,
    onChange,
    handleSelect,
    onInputClick,
    onInputBlur,
    onInputFocus,
    onInputKeyDown,
    isDropdownOpen,
    closeDropdown,
  } = useAddressAutocomplete({
    onCurrentLocationClick,
    onPlaceSelected,
    resetAddress,
    isMaxMobile,
  });

  if (!isLoaded) {
    return null;
  }

  return (
    <Root onClickOutside={closeDropdown}>
      <ScreenReaderNotificator>{t('type_address_for_delivery')}</ScreenReaderNotificator>
      <Input
        value={address}
        autoFocus={isAddAddressMode && !currentAddressKey}
        type="text"
        role="combobox"
        aria-controls="homePage_DropDownAddress"
        aria-autocomplete="list"
        aria-expanded={isInputFocused}
        autoComplete="off"
        isAddAddressMode={isAddAddressMode}
        ref={inputRef}
        onChange={onChange}
        onClick={onInputClick}
        onBlur={onInputBlur}
        onFocus={onInputFocus}
        onKeyDown={onInputKeyDown}
        placeholder={placeholder}
      />
      <StyledSearchIconSvg />
      {isDropdownOpen && (
        <DropdownContainer>
          {predictions.map((suggestion, index) => {
            if (!suggestion) {
              return null;
            }
            const isCurrentLocation = suggestion.placeId === CURRENT_LOCATION_PLACE_ID;
            const isHighlighted = index === highlightedIndex;
            return (
              <SuggestionItem
                isCurrentLocation={isCurrentLocation}
                id={suggestion.placeId}
                key={suggestion.placeId}
                isAddAddressMode={isAddAddressMode}
                active={isHighlighted}
                aria-selected={isHighlighted}
                onClick={() => handleSelect(suggestion.text.text, suggestion.placeId)}
              >
                <SuggestionItemIcon isCurrentLocation={isCurrentLocation} />

                <SuggestionResultWithBoldParts
                  matchedSubstrings={suggestion.text.matches}
                  description={suggestion.text.text}
                />
              </SuggestionItem>
            );
          })}
          <PoweredByGoogleWrapper shouldShow={predictions.length > 1}>
            <PoweredByGoogleImg alt="Powered by google icon" />
          </PoweredByGoogleWrapper>
        </DropdownContainer>
      )}
    </Root>
  );
};

export default CustomAddressAutoComplete;
